import { motion } from 'framer-motion';
import { Sparkles, Truck, PartyPopper, Droplets, Leaf, Zap } from 'lucide-react';

const AboutSection = () => {
  const features = [
    {
      icon: Truck,
      title: 'Festival Ready',
      description: 'High-capacity mobile units ready for crowds of any size. From mid-size concerts to massive multi-day music festivals.',
    },
    {
      icon: Droplets,
      title: '100% Juice Based',
      description: 'Pure, refreshing slushies made from real fruit juice. No high-fructose corn syrup, no artificial sweeteners.',
    },
    {
      icon: Zap,
      title: 'Vitamin Enriched',
      description: 'Packed with vitamins A, C, D and electrolytes to keep your attendees hydrated and energized all day.',
    },
  ];

  const healthBenefits = [
    { label: 'No Added Sugar', icon: '✓' },
    { label: 'Vegan', icon: '🌱' },
    { label: 'Gluten Free', icon: '✓' },
    { label: 'Electrolytes', icon: '⚡' },
    { label: 'Kid Friendly', icon: '👨‍👩‍👧‍👦' },
  ];

  return (
    <section 
      id="about" 
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#050505] noise-bg"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#CCFF00] text-sm font-medium tracking-widest uppercase mb-4 block">
            Why Choose Us
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">HEALTHIER</span>{' '}
            <span className="text-gradient">HYDRATION</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto leading-relaxed">
            SLUSHd is the go-to refreshment solution for music festivals, concerts, and large-scale events. 
            Our 100% juice-based slushies are designed to keep your attendees cool, hydrated, and energized 
            without the sugar crash. Family-friendly and kid-approved, our drinks are perfect for health-conscious 
            event organizers who want to offer something better than typical concession drinks.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass-card glass-card-hover rounded-2xl p-8 text-center transition-all duration-300"
              data-testid={`about-feature-${index}`}
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#CCFF00]/10 flex items-center justify-center">
                <feature.icon className="w-8 h-8 text-[#CCFF00]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 uppercase tracking-wide">
                {feature.title}
              </h3>
              <p className="text-white/60 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { value: '100+', label: 'Events Served' },
            { value: '500K+', label: 'Happy Attendees' },
            { value: '0g', label: 'Added Sugar' },
            { value: '100%', label: 'Juice Based' },
          ].map((stat, index) => (
            <div key={stat.label} className="text-center" data-testid={`about-stat-${index}`}>
              <div className="text-4xl md:text-5xl font-black text-[#CCFF00] mb-2">
                {stat.value}
              </div>
              <div className="text-white/50 text-sm uppercase tracking-widest">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Health Benefits Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 flex flex-wrap justify-center gap-4"
        >
          {healthBenefits.map((benefit) => (
            <div 
              key={benefit.label}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30"
            >
              <span className="text-[#CCFF00]">{benefit.icon}</span>
              <span className="text-white/90 text-sm font-medium">{benefit.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Decorative Element */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-20 pointer-events-none">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/flagged/photo-1572422476918-416e3aecb6a7?q=80&w=2070&auto=format&fit=crop)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            maskImage: 'linear-gradient(to left, black, transparent)',
            WebkitMaskImage: 'linear-gradient(to left, black, transparent)',
          }}
        />
      </div>
    </section>
  );
};

export default AboutSection;
